import { useEffect, useState } from "react";
import { api } from "./api";
import type { ActivityKind, PlanDay } from "./types";

const TYPES: ActivityKind[] = ["gym", "run", "bike", "rest", "swim"];

export function PlanEditModal({
  day,
  onClose,
  onSaved,
}: {
  day: PlanDay;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [type, setType] = useState<ActivityKind>(day.type);
  const [target, setTarget] = useState(String(day.target));
  const [title, setTitle] = useState(day.training.title);
  const [icon, setIcon] = useState(day.training.icon ?? "");
  const [time, setTime] = useState(day.training.time ?? "");
  const [detail, setDetail] = useState(day.training.detail ?? "");
  const [note, setNote] = useState(day.training.note ?? "");
  const [calBurn, setCalBurn] = useState(String(day.training.calBurn ?? 0));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function save() {
    setSaving(true);
    setErr(null);
    try {
      await api.patchPlan(day.dateISO, {
        type,
        target: Number(target) || 0,
        training: {
          title: title.trim(),
          icon: icon.trim() || undefined,
          time: time.trim() || undefined,
          detail: detail.trim() || undefined,
          note: note.trim() || undefined,
          calBurn: Number(calBurn) || 0,
        },
      });
      onSaved();
    } catch (e: any) {
      setErr(String(e?.message ?? e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h2>Edit plan — {day.date}</h2>
        <p style={{ fontSize: 12, color: "var(--muted)" }}>
          Title can use “+” to enumerate activities (e.g. “Platformr + Zone 2 Run + Golf”).
          Each phrase becomes a tickable exercise row.
        </p>

        <label>
          Activity type
          <select value={type} onChange={(e) => setType(e.target.value as ActivityKind)}>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>

        <label>
          Title
          <input value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 1fr", gap: 8 }}>
          <label>
            Icon
            <input value={icon} onChange={(e) => setIcon(e.target.value)} placeholder="🏋️" />
          </label>
          <label>
            Time
            <input value={time} onChange={(e) => setTime(e.target.value)} placeholder="6pm" />
          </label>
          <label>
            kcal
            <input value={calBurn} onChange={(e) => setCalBurn(e.target.value)} type="number" />
          </label>
        </div>

        <label>
          Detail
          <textarea
            value={detail}
            onChange={(e) => setDetail(e.target.value)}
            rows={3}
            placeholder="e.g. STRENGTH Upper Focus with James"
          />
        </label>
        <label>
          Note
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="anything to remember"
          />
        </label>

        <label>
          Daily kcal target
          <input type="number" value={target} onChange={(e) => setTarget(e.target.value)} />
        </label>

        {err && <div className="err" style={{ color: "var(--red)", fontSize: 12 }}>{err}</div>}

        <div className="sheet-actions">
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-accent" onClick={save} disabled={saving || !title.trim()}>
            {saving ? "…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
